import React, { useEffect, useState } from "react";
import { Box } from "@chakra-ui/react";

import QuotesToolbar     from "../components/Quotes/QuotesToolbar";
import QuotesTable       from "../components/Quotes/QuotesTable";
import PaginationBar     from "../components/Quotes/PaginationBar";
import LoadingOverlay    from "../components/Quotes/LoadingOverlay";
import QuoteDetailModal  from "../components/Quotes/QuoteDetailModal";

import { getQuotesByUserId } from "../services/api";
import {
  filterQuotes, sortQuotes, paginate,
} from "../services/quoteHelpers";

/* colonne visibili di default */
const defaultCols = {
  id: true, date: true, total_amount: true, discount_extra: true,
  quote_status: true, note: true, user: true,
};

export default function Quotes() {
  const [quotes, setQuotes]               = useState([]);
  const [visibleQuotes, setVisibleQuotes] = useState([]);

  const [searchTerm, setSearchTerm]       = useState("");
  const [sortConfig, setSortConfig]       = useState({ key: "", direction: "" });
  const [itemsPerPage, setItemsPerPage]   = useState(15);
  const [currentPage, setCurrentPage]     = useState(1);
  const [startDate, setStartDate]         = useState(null);
  const [endDate, setEndDate]             = useState(null);
  const [columns, setColumns]             = useState(defaultCols);

  const [selectedQuote, setSelectedQuote] = useState(null);
  const [isDetailOpen, setIsDetailOpen]   = useState(false);
  const [isLoading, setIsLoading]         = useState(true);

  /* --- fetch iniziale --- */
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const raw = localStorage.getItem("user");
      if (!raw) return setIsLoading(false);

      try {
        const currentUser = JSON.parse(raw);
        if (!currentUser?.id) return setIsLoading(false);

        const { data } = await getQuotesByUserId(currentUser.id);
        const enriched = (data || []).map((q) => ({
          ...q,
          user: { name: currentUser.name },
        }));
        setQuotes(enriched);
      } catch (err) {
        console.error("Errore fetch quotes", err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  /* --- filtri, sort, paginazione --- */
  useEffect(() => {
    let tmp = filterQuotes(quotes, searchTerm, startDate, endDate);
    tmp     = sortQuotes(tmp, sortConfig);
    tmp     = paginate(tmp, currentPage, itemsPerPage);
    setVisibleQuotes(tmp);
  }, [quotes, searchTerm, startDate, endDate,
      sortConfig, currentPage, itemsPerPage]);

  /* --- handlers --- */
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const toggleColumn = (col) =>
    setColumns((c) => ({ ...c, [col]: !c[col] }));

  const totalPages = Math.max(1, Math.ceil(quotes.length / itemsPerPage));

  /* --- scorciatoie data --- */
  const resetToday = () => {
    const d = new Date();
    setStartDate(d); setEndDate(d); setCurrentPage(1);
  };
  const resetLast7 = () => {
    const d = new Date(); const d7 = new Date();
    d7.setDate(d.getDate() - 7);
    setStartDate(d7); setEndDate(d); setCurrentPage(1);
  };

  return (
    <Box py={8}>
      {isLoading && <LoadingOverlay />}

      <QuotesToolbar
        searchTerm={searchTerm}
        onSearch={(v) => { setSearchTerm(v); setCurrentPage(1); }}
        startDate={startDate} endDate={endDate}
        onStartDate={(d) => { setStartDate(d); setCurrentPage(1); }}
        onEndDate={(d) => { setEndDate(d);   setCurrentPage(1); }}
        itemsPerPage={itemsPerPage}
        onPageSize={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
        columns={columns} toggleColumn={toggleColumn}
        resetToday={resetToday} resetLast7={resetLast7}
      />

      <QuotesTable
        quotes={visibleQuotes} columns={columns}
        sortConfig={sortConfig} onSort={handleSort}
        onDetail={(q) => { setSelectedQuote(q); setIsDetailOpen(true); }}
      />

      <PaginationBar
        currentPage={currentPage} totalPages={totalPages}
        totalQuotes={quotes.length} visibleQuotes={visibleQuotes.length}
        itemsPerPage={itemsPerPage} onPageChange={setCurrentPage}
      />

      {selectedQuote && (
        <QuoteDetailModal
          isOpen={isDetailOpen}
          onClose={() => setIsDetailOpen(false)}
          quote={selectedQuote}
        />
      )}
    </Box>
  );
}
